import { useState, useEffect, useRef } from 'react'

/**
 * Global cache for image blob URLs.
 * Key: storage path (stable identifier extracted from signed URL)
 * Value: blob URL created from fetched image data
 */
const imageCache = new Map<string, string>()

/**
 * In-flight fetch promises to prevent duplicate fetches for the same image.
 */
const fetchInProgress = new Map<string, Promise<string>>()

/**
 * Extract the stable storage path from a GCS signed URL.
 * Signed URLs use the unified structure:
 *   - https://storage.googleapis.com/bucket/users/uid/images/image_id/original.jpg?X-Goog-...
 *   - https://storage.googleapis.com/bucket/users/uid/images/image_id/cutout.png?X-Goog-...
 * Also supports legacy onboarding-photos/ paths for backward compatibility.
 * We extract the path portion before the query string.
 */
function extractStoragePath(signedUrl: string): string | null {
  try {
    const url = new URL(signedUrl)
    // Match storage path patterns: users/ (primary) or onboarding-photos/ (legacy)
    const match = url.pathname.match(/\/((?:users|onboarding-photos)\/[^?]+)/)
    return match?.[1] || null
  } catch {
    // Fallback: try regex directly on the string
    const match = signedUrl.match(/(?:users|onboarding-photos)\/[^?]+/)
    return match?.[0] || null
  }
}

/**
 * Fetch an image and convert it to a blob URL.
 * Uses deduplication to prevent multiple fetches of the same image.
 */
async function fetchAndCacheImage(signedUrl: string, cacheKey: string): Promise<string> {
  // Check if already cached
  const cached = imageCache.get(cacheKey)
  if (cached) return cached

  // Check if fetch is already in progress
  const inProgress = fetchInProgress.get(cacheKey)
  if (inProgress) return inProgress

  // Start new fetch
  const fetchPromise = (async () => {
    try {
      const response = await fetch(signedUrl)
      if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`)
      
      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      
      // Store in cache
      imageCache.set(cacheKey, objectUrl)
      return objectUrl
    } finally {
      // Clean up in-flight tracker
      fetchInProgress.delete(cacheKey)
    }
  })()

  fetchInProgress.set(cacheKey, fetchPromise)
  return fetchPromise
}

/**
 * Hook that caches images as blob URLs to prevent re-fetching
 * when signed URLs change but the underlying image is the same.
 * 
 * This solves the flickering issue with GCS signed URLs where:
 * - The same image has different URL signatures on each API call
 * - React sees a new src string and triggers a re-fetch
 * - The image flickers even though the content is identical
 * 
 * @param signedUrl - The signed URL from the backend (may change frequently)
 * @returns A stable blob URL that only changes when the actual image changes
 */
export function useCachedImage(signedUrl: string | undefined): string | undefined {
  const [cachedUrl, setCachedUrl] = useState<string | undefined>(() => {
    if (!signedUrl) return undefined
    const cacheKey = extractStoragePath(signedUrl)
    return cacheKey ? imageCache.get(cacheKey) : undefined
  })
  
  // Track the last processed storage path to avoid unnecessary fetches
  const lastCacheKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!signedUrl) {
      setCachedUrl(undefined)
      lastCacheKeyRef.current = null
      return
    }

    const cacheKey = extractStoragePath(signedUrl)
    
    // If we can't extract a cache key, use the URL directly
    if (!cacheKey) {
      setCachedUrl(signedUrl)
      lastCacheKeyRef.current = null
      return
    }

    // Skip if we already processed this storage path
    if (cacheKey === lastCacheKeyRef.current) {
      return
    }

    // Check if already cached in global cache
    const cached = imageCache.get(cacheKey)
    if (cached) {
      setCachedUrl(cached)
      lastCacheKeyRef.current = cacheKey
      return
    }

    // Fetch and cache the image
    let cancelled = false
    
    fetchAndCacheImage(signedUrl, cacheKey)
      .then(blobUrl => {
        if (!cancelled) {
          setCachedUrl(blobUrl)
          lastCacheKeyRef.current = cacheKey
        }
      })
      .catch(() => {
        // On error, fall back to the original signed URL
        if (!cancelled) {
          setCachedUrl(signedUrl)
          lastCacheKeyRef.current = cacheKey
        }
      })

    return () => {
      cancelled = true
    }
  }, [signedUrl])

  return cachedUrl
}

/**
 * Preload an image into the cache without rendering it.
 * Useful for preloading images before they're displayed.
 */
export function preloadImage(signedUrl: string): Promise<void> {
  const cacheKey = extractStoragePath(signedUrl)
  if (!cacheKey) return Promise.resolve()
  
  if (imageCache.has(cacheKey)) return Promise.resolve()
  
  return fetchAndCacheImage(signedUrl, cacheKey).then(() => {})
}

/**
 * Clear a specific image from the cache (e.g., after uploading a new photo).
 */
export function invalidateCachedImage(signedUrl: string): void {
  const cacheKey = extractStoragePath(signedUrl)
  if (!cacheKey) return
  
  const existingUrl = imageCache.get(cacheKey)
  if (existingUrl) {
    URL.revokeObjectURL(existingUrl)
    imageCache.delete(cacheKey)
  }
}

/**
 * Clear the entire image cache.
 */
export function clearImageCache(): void {
  imageCache.forEach(blobUrl => URL.revokeObjectURL(blobUrl))
  imageCache.clear()
}


